'use client';

import { useState, Suspense, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter, usePathname } from '@/i18n/routing';
import { Box, Container, Grid, Typography, Chip, Tabs, Tab } from "@mui/material";
import ContactInfo from '@/components/ui/contact/ContactInfo';
import ContactForm from '@/components/ui/contact/ContactForm';
import DemoRequestForm from '@/components/ui/contact/DemoRequestForm';
import { useTranslations } from 'next-intl';

function ContactTabsWrapper() {
    const t = useTranslations('ContactPage.ContactContent');
    const searchParams = useSearchParams();
    const router = useRouter();
    const pathname = usePathname();

    const initialTab = searchParams.get('tab') === 'demo' ? 1 : 0;
    const [activeTab, setActiveTab] = useState(initialTab);

    // Keep activeTab in sync with query parameter changes
    useEffect(() => {
        const isDemo = searchParams.get('tab') === 'demo';
        setActiveTab(isDemo ? 1 : 0);
    }, [searchParams]);

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
        if (newValue === 1) {
            router.replace(`${pathname}?tab=demo`, { scroll: false });
        } else {
            router.replace(pathname, { scroll: false });
        }
    };

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        p: 0.5,
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                        '& .MuiTabs-indicator': {
                            display: 'none',
                        },
                        '& .MuiTab-root': {
                            fontWeight: 700,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            borderRadius: '8px',
                            py: 1.5,
                            color: '#64748b',
                            transition: 'all 0.2s ease-in-out',
                            '&.Mui-selected': {
                                color: '#ffffff',
                                backgroundColor: '#132c4b',
                                boxShadow: '0 4px 6px -1px rgba(19, 44, 75, 0.2)',
                            },
                        },
                    }}
                >
                    <Tab label={t('tab_current')} />
                    <Tab label={t('tab_demo')} />
                </Tabs>
            </Box>

            {activeTab === 0 ? <ContactForm /> : <DemoRequestForm />}
        </Box>
    );
}

export default function ContactContent() {
    const t = useTranslations('ContactPage.ContactContent');
    const ACCENT = '#F15859';
    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', pb: 12 }}>
            {/* Header */}
            <Box sx={{ backgroundColor: '#132c4b', color: 'white', py: { xs: 8, md: 8 }, mb: { xs: 8, md: 10 } }}>
                <Container maxWidth="lg">
                    <Chip
                        label={t('chip')}
                        sx={{
                            bgcolor: ACCENT,
                            color: "white",
                            fontWeight: 700,
                            mb: 3,
                        }}
                    />
                    <Typography
                        component="h1"
                        sx={{
                            fontSize: { xs: "24px", md: "36px" },
                            fontWeight: 800,
                            lineHeight: 1.1,
                            mb: 2,
                        }}
                    >
                        {t('title')}
                    </Typography>
                    <Typography
                        sx={{
                            color: "rgba(255,255,255)",
                            mb: 2,
                            maxWidth: "80%",
                            fontSize: "14px",
                        }}
                    >
                        {t('description_1')}
                        <br />
                        {t('description_2')}
                        <br />
                        {t('description_3')}
                    </Typography>
                </Container>
            </Box>

            {/* Main Content */}
            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    {/* Left Side: Info */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <ContactInfo />
                    </Grid>

                    {/* Right Side: Form with Tabs */}
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Suspense fallback={<ContactForm />}>
                            <ContactTabsWrapper />
                        </Suspense>
                    </Grid>
                </Grid>
            </Container>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "ContactPage",
                        "@id": "https://airxpress.jp/contact",
                        "url": "https://airxpress.jp/contact",
                        "name": "無料相談・お見積り",
                        "inLanguage": "ja",
                        "about": {
                            "@type": "Service",
                            "name": "民泊運営代行"
                        }
                    })
                }}
            />
        </Box>
    );
}
